import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../services/firebase';

const WholesaleAuthContext = createContext();

export const useWholesaleAuth = () => {
  const context = useContext(WholesaleAuthContext);
  if (!context) {
    throw new Error('useWholesaleAuth must be used within a WholesaleAuthProvider');
  }
  return context;
};

export const WholesaleAuthProvider = ({ children }) => {
  const [currentWholesaleUser, setCurrentWholesaleUser] = useState(null);
  const [wholesaleCustomer, setWholesaleCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored wholesale session on app load
    const checkWholesaleSession = () => {
      try {
        const storedSession = localStorage.getItem('wholesaleSession');
        if (storedSession) {
          const { user, customer } = JSON.parse(storedSession);
          setCurrentWholesaleUser(user);
          setWholesaleCustomer(customer);
        }
      } catch (error) {
        console.error('Error loading wholesale session:', error);
        localStorage.removeItem('wholesaleSession');
      }
      setLoading(false);
    };

    checkWholesaleSession();
  }, []);

  const wholesaleLogin = async (email, accountNumber, password) => {
    try {
      // Find the customer by email and account number
      const customerQuery = query(
        collection(db, 'wholesaleCustomers'),
        where('wholesaleEmail', '==', email),
        where('accountNumber', '==', accountNumber),
        where('status', 'in', ['approved', 'demo', 'trial'])
      );
      const customerSnapshot = await getDocs(customerQuery);
      
      if (customerSnapshot.empty) {
        throw new Error('Invalid email, account number, or account not approved');
      }
      
      const customerDoc = customerSnapshot.docs[0];
      const customerData = { id: customerDoc.id, ...customerDoc.data() };
      
      // Check if password matches (no Firebase Auth involved)
      if (customerData.wholesalePassword !== password) {
        throw new Error('Invalid credentials');
      }
      
      // Create a virtual user object for wholesale session
      const virtualWholesaleUser = {
        uid: customerData.wholesaleUid,
        email: customerData.wholesaleEmail,
        isWholesale: true
      };
      
      setCurrentWholesaleUser(virtualWholesaleUser);
      setWholesaleCustomer(customerData);
      
      // Store session in localStorage for persistence
      localStorage.setItem('wholesaleSession', JSON.stringify({
        user: virtualWholesaleUser,
        customer: customerData
      }));
      
      return { user: virtualWholesaleUser, customer: customerData };
    } catch (error) {
      console.error('Wholesale login error:', error);
      throw error;
    }
  };

  const wholesaleLogout = async () => {
    try {
      // Clear wholesale session (no Firebase Auth signout needed)
      localStorage.removeItem('wholesaleSession');
      setCurrentWholesaleUser(null);
      setWholesaleCustomer(null);
    } catch (error) {
      console.error('Wholesale logout error:', error);
      throw error;
    }
  };

  const refreshWholesaleCustomer = async () => {
    if (!wholesaleCustomer?.id) return;
    
    try {
      const customerDoc = await getDoc(doc(db, 'wholesaleCustomers', wholesaleCustomer.id));
      if (customerDoc.exists()) {
        const updatedCustomerData = { id: customerDoc.id, ...customerDoc.data() };
        setWholesaleCustomer(updatedCustomerData);
        
        // Update localStorage with fresh data
        const storedSession = localStorage.getItem('wholesaleSession');
        if (storedSession) {
          const session = JSON.parse(storedSession);
          session.customer = updatedCustomerData;
          localStorage.setItem('wholesaleSession', JSON.stringify(session));
        }
        
        return updatedCustomerData;
      }
    } catch (error) {
      console.error('Error refreshing customer data:', error);
      throw error;
    }
  };

  const value = {
    currentWholesaleUser,
    wholesaleCustomer,
    loading,
    login: wholesaleLogin,
    logout: wholesaleLogout,
    refreshCustomer: refreshWholesaleCustomer
  };

  return (
    <WholesaleAuthContext.Provider value={value}>
      {children}
    </WholesaleAuthContext.Provider>
  );
};